import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditAppointmentNotesForm, EditAppointmentNotesSchema, Input } from '$features/appointments/forms/edit-notes/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentShowOptions, appointmentShowQueryKey, appointmentUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { AppointmentsFilterParam } from '../enums/filter-params'

export function EditAppointmentNotesModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...appointmentShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        [AppointmentsFilterParam.IncludeUnapproved]: searchParams.get(AppointmentsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updateAppointment, isPending: isUpdatingAppointment } = useMutation({
    ...appointmentUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appointmentShowQueryKey({
          path: {
            id: Number(id),
          },
          query: {
            [AppointmentsFilterParam.IncludeUnapproved]: searchParams.get(AppointmentsFilterParam.IncludeUnapproved) === '1',
          },
        }),
      })
      ToastHelper.success('Appointment notes updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update appointment notes.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditAppointmentNotesSchema) => {
    updateAppointment({
      path: {
        id: Number(id),
      },
      body: {
        notes: values[Input.Notes],
      },
    })
  }

  const appointment = data?.data

  if (!appointment) {
    return null
  }

  return (
    <EditAppointmentNotesForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingAppointment}
      defaultValues={{
        [Input.Notes]: appointment.notes ?? undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Notes
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditAppointmentNotesForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditAppointmentNotesForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditAppointmentNotesForm>
  )
}
